import { motion } from 'framer-motion';
import useGameStore from '@/store/gameStore';
import websocketService from '@/services/websocket';

function PlayerList() {
  const { gameState, user } = useGameStore();

  if (!gameState) return null;

  const players = Object.values(gameState.players);

  return (
    <div className="card">
      <h2 className="text-lg font-semibold text-gray-700 mb-4">
        Players ({players.length}/
        {gameState.config.aiPlayerCount + players.filter((p) => !p.isAi).length})
      </h2>

      <div className="space-y-3">
        {players.map((player, index) => {
          const isCurrentUser = player.id === user?.id;
          const isGuesser = player.id === gameState.round?.activeGuesserId;
          const hasSubmittedClue = gameState.round?.clues[player.id] !== undefined;

          return (
            <motion.div
              key={player.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              data-testid={`player-${player.id}`}
              className={`
                flex items-center gap-3 p-3 rounded-lg transition-all
                ${isCurrentUser ? 'bg-primary-50 border-2 border-primary-200' : 'bg-gray-50'}
                ${isGuesser ? 'ring-2 ring-green-400' : ''}
              `}
            >
              {/* Avatar */}
              <div
                className={`
                  w-10 h-10 rounded-full flex items-center justify-center text-xl overflow-hidden
                  ${player.isAi ? 'bg-gray-300' : 'bg-primary-400'}
                  ${player.connectionStatus === 'OFFLINE' ? 'opacity-50' : ''}
                `}
              >
                {player.avatar.startsWith('http') ? (
                  <img
                    src={player.avatar}
                    alt={player.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span>{player.avatar}</span>
                )}
              </div>

              {/* Player Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-gray-800">
                    {player.name}
                    {isCurrentUser && ' (You)'}
                  </p>
                  {player.isHost && (
                    <span className="text-xs bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded-full">
                      HOST
                    </span>
                  )}
                  {player.isAi && (
                    <span className="text-xs bg-gray-400 text-white px-2 py-0.5 rounded-full">
                      AI
                    </span>
                  )}
                </div>

                {/* Status Indicators */}
                <div className="flex items-center gap-2 mt-1">
                  {player.connectionStatus === 'OFFLINE' && (
                    <span className="text-xs text-gray-500">Disconnected</span>
                  )}
                  {isGuesser && (
                    <span className="text-xs text-green-600 font-semibold">🤔 Guesser</span>
                  )}
                  {hasSubmittedClue && !isGuesser && gameState.phase === 'WRITING' && (
                    <span className="text-xs text-blue-600">✍️ Clue submitted</span>
                  )}
                </div>
              </div>

              {/* Connection Indicator */}
              <div
                className={`
                  w-2 h-2 rounded-full
                  ${player.connectionStatus === 'ONLINE' ? 'bg-green-500' : 'bg-gray-400'}
                `}
              />
            </motion.div>
          );
        })}
      </div>

      {/* Add AI Players Button (Lobby Only) */}
      {gameState.phase === 'LOBBY' && gameState.hostId === user?.id && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600 mb-2">
            AI Players: {gameState.config.aiPlayerCount}/5
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => {
                const newCount = Math.min(gameState.config.aiPlayerCount + 1, 5);
                websocketService.updateSettings({ aiPlayerCount: newCount });
              }}
              disabled={gameState.config.aiPlayerCount >= 5}
              className="btn-secondary text-sm py-2"
              data-testid="btn-add-ai"
            >
              Add AI
            </button>
            <button
              onClick={() => {
                const newCount = Math.max(gameState.config.aiPlayerCount - 1, 0);
                websocketService.updateSettings({ aiPlayerCount: newCount });
              }}
              disabled={gameState.config.aiPlayerCount === 0}
              className="btn-secondary text-sm py-2"
              data-testid="btn-remove-ai"
            >
              Remove AI
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlayerList;
